import { INVOICE_CATEGORY } from "@/lib/invoiceCategories";

/**
 * Server invoice payloads -> the shape the UI renders.
 *
 * Two things the old app did here are deliberately not repeated:
 *
 * 1. It formatted values inside `transformResponse` — amounts became strings
 *    like "£5,100.00" and dates became "18/03/2026". That made the cached data
 *    unusable for sorting, comparison, or arithmetic, and locked the display
 *    locale into the cache. Amounts stay numbers and dates stay ISO here;
 *    formatting happens in the component that renders them.
 *
 * 2. It copied the pagination block (`pageSettings`) onto every single row, so
 *    a 16-row page carried 16 identical copies of the page count. Pagination is
 *    returned once, beside the rows.
 */

/** The API returns extracted values as a flat `data` array of named fields. */
export const INVOICE_FIELDS = {
  companyName: "Company Name",
  companyAddress: "Company Address",
  invoiceNumber: "Invoice Number",
  invoiceDate: "Invoice Date",
  orderNumber: "Purchase Order",
  vatNumber: "Vat Number",
  netAmount: "Net Amount",
  taxValue: "Tax Value",
  totalAmount: "Total Amount",
  carriage: "Carriage",
};

/** `[{ fieldName, fieldValue, errorMessage }]` -> `{ values, errors }` keyed by our field names. */
export function readInvoiceFields(data) {
  const values = {};
  const errors = {};

  // `data` is whatever the endpoint sent. A non-array has no `.find` and a null
  // entry has no `.fieldName` — and this runs inside `transformResponse`, where
  // a throw escapes the render tree and the error boundary never sees it.
  const fields = Array.isArray(data) ? data : [];

  Object.entries(INVOICE_FIELDS).forEach(([key, fieldName]) => {
    const field = fields.find((item) => item?.fieldName === fieldName);
    values[key] = field?.fieldValue ?? null;
    if (field?.errorMessage) errors[key] = field.errorMessage;
  });

  return { values, errors };
}

function toNumber(value) {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Normalises any date the API returns to `YYYY-MM-DD`, or null. */
function toIsoDate(value) {
  if (!value) return null;

  const pad = (n) => String(n).padStart(2, "0");
  const fromLocal = (date) =>
    Number.isNaN(date.getTime())
      ? null
      : `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  // Epoch milliseconds and Date objects name an instant: read it in local time.
  if (typeof value === "number" || value instanceof Date) return fromLocal(new Date(value));

  // An ISO-style string carries its calendar date up front, so that date is the
  // answer. Going through `new Date` and `toISOString` instead read a
  // timezone-less "2024-05-01" as local midnight and converted it to UTC, one day
  // earlier east of Greenwich, so every save and reload walked the date back.
  const text = String(value).trim();
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (iso) {
    const [, y, m, d] = iso;
    const check = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
    const valid =
      check.getUTCFullYear() === Number(y) &&
      check.getUTCMonth() === Number(m) - 1 &&
      check.getUTCDate() === Number(d);
    return valid ? `${y}-${m}-${d}` : null;
  }

  // Free text ("01 May 2024") parses as local time, so it is read back locally.
  return fromLocal(new Date(text));
}

/**
 * Labels arrive as `{ text, colorHex, icon, redirectPath }`. Anything without
 * text has nothing to render, so it is dropped here rather than leaving every
 * consumer to produce an empty badge.
 */
function mapLabels(labels) {
  return (labels ?? [])
    .filter((label) => label?.text?.trim())
    .map((label) => ({
      text: label.text.trim(),
      color: label.colorHex?.trim() || null,
      icon: label.icon?.trim() || null,
      redirectPath: label.redirectPath?.trim() || null,
    }));
}

/**
 * The duplicate reference is only usable if it carries a guid to link to; a
 * truthy-but-empty object would otherwise render as a warning about nothing.
 */
function mapDuplicateOf(duplicate) {
  if (!duplicate?.guid) return null;
  return { guid: duplicate.guid, invoiceNumber: duplicate.invoiceNumber ?? null };
}

export function extractPagination(response) {
  return {
    page: response?.page ?? 1,
    pageSize: response?.pageSize ?? 0,
    totalPages: response?.totalPages ?? 0,
    total: response?.total ?? 0,
  };
}

/**
 * One row mapper for every invoice list. The old app had three near-identical
 * copies of this (`getInvoices`, `getMyInvoices`, `getStarredInvoices`) that had
 * already drifted — only one of them mapped `failureType`, and only one mapped
 * `assignedTo`.
 */
export function mapInvoiceRow(item, currentUserGuid) {
  const { values } = readInvoiceFields(item?.data);

  return {
    id: item?.guid,
    guid: item?.guid,
    invoiceNumber: values.invoiceNumber,
    clientName: item?.clientName ?? null,
    companyName: values.companyName,
    companyAddress: values.companyAddress,
    invoiceDate: toIsoDate(values.invoiceDate),
    createdOn: toIsoDate(item?.createdOn),
    orderNumber: values.orderNumber,
    vatNumber: values.vatNumber,
    netAmount: toNumber(values.netAmount),
    taxValue: toNumber(values.taxValue),
    carriage: toNumber(values.carriage),
    totalAmount: toNumber(values.totalAmount),
    currencySymbol: item?.currencySymbol ?? "£",

    reviewStatus: item?.reviewStatus?.name ?? null,
    reviewStatusId: item?.reviewStatus?.id ?? null,
    processingStatus: item?.processingStatus?.name ?? null,
    processingStatusId: item?.processingStatus?.id ?? null,
    failureType: item?.failureType ?? null,

    categoryId: item?.category?.id ?? null,
    categoryGuid: item?.category?.guid ?? null,
    categoryName: item?.category?.name ?? null,

    assignedTo: item?.assignedTo?.name ?? null,
    assignedToId: item?.assignedTo?.id ?? null,
    assignedToGuid: item?.assignedTo?.guid ?? null,
    assignedToMe: Boolean(currentUserGuid) && item?.assignedTo?.guid === currentUserGuid,

    labels: mapLabels(item?.labels),
    comments: item?.comments ?? [],
    duplicateOfInvoice: mapDuplicateOf(item?.duplicateOfInvoice),
    starred: Boolean(item?.isStarredInvoice),
  };
}

export function mapInvoiceList(response, currentUserGuid) {
  return {
    items: (response?.items ?? []).map((item) => mapInvoiceRow(item, currentUserGuid)),
    pagination: extractPagination(response),
  };
}

/** The starred endpoint wraps each invoice in a join row. */
export function mapStarredInvoiceList(response, currentUserGuid) {
  return {
    items: (response?.items ?? [])
      .filter((entry) => entry?.invoice)
      .map((entry) => ({ ...mapInvoiceRow(entry.invoice, currentUserGuid), starred: true })),
    pagination: extractPagination(response),
  };
}

/**
 * Detail view. The old app pushed a single object through a `createEntityAdapter`
 * and then had every caller unpack it with `entities[ids[0]]`. It is one record —
 * it is returned as one record.
 */
export function mapInvoiceDetail(item, currentUserGuid) {
  const { values, errors } = readInvoiceFields(item?.data);

  return {
    ...mapInvoiceRow(item, currentUserGuid),
    isSampleInvoice: item?.category?.id === INVOICE_CATEGORY.SAMPLE_INVOICE,
    isApproved: typeof item?.isApproved === "boolean" ? item.isApproved : null,
    fieldValues: values,
    fieldErrors: errors,
    starred: Boolean(item?.isStarredInvoice),
    fileName: item?.file?.name ? `${item.file.name}.pdf` : `${values.invoiceNumber ?? "invoice"}.pdf`,
    file: item?.file ?? null,
    raw: item,
  };
}

export function mapCreationLog(item) {
  return {
    id: item?.id,
    message: item?.message,
    createdOn: item?.createdOn ?? null,
    status: item?.status ?? null,
    invoiceGuid: item?.invoice?.guid ?? null,
    createdBy: item?.createdBy?.name ?? null,
  };
}
